import { useTranslation } from "react-i18next";
import { type NavigationNodeDTO } from "~/domains/navigation/types/NavigationTypes";
import { Fragment } from "react";
import { sendGAEvent } from "~/app/analytics/ga/ga";
import { Link, LinkType } from "../Link";
import { styled, Typography } from "~/common/mui";

interface Props extends PageFooterProps {
  className?: string;
}

// Root container with top border and padding
const Root = styled("div")(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(3),
  [theme.breakpoints.up("desktop-s")]: {
    padding: theme.spacing(4),
  },
}));

// Navigation wrapper with flex layout
const Navigation = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("tablet")]: {
    display: "flex",
    marginLeft: theme.spacing(-1.5),
    marginRight: theme.spacing(-1.5),
    "& > *": {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
    },
  },
  [theme.breakpoints.up("desktop-s")]: {
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
    "& > *": {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
}));

// Links section with responsive layout
const LinksSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  "& > * + *": {
    marginTop: theme.spacing(4),
  },
  [theme.breakpoints.up("phone")]: {
    flexDirection: "row",
    "& > *": {
      flex: "1 1 0",
    },
    "& > * + *": {
      marginLeft: theme.spacing(2),
      marginTop: 0,
    },
  },
  [theme.breakpoints.up("tablet")]: {
    flex: "2 0 66.6667%",
    "& > * + *": {
      marginLeft: theme.spacing(3),
    },
  },
  [theme.breakpoints.up("desktop-s")]: {
    "& > * + *": {
      marginLeft: theme.spacing(4),
    },
  },
}));

// Links column
const LinksColumn = styled("ul")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: 0,
  marginBottom: 0,
  listStyle: "none",
  "& > *:first-of-type": {
    marginTop: 0,
  },
  "& > * + *": {
    marginTop: theme.spacing(2),
  },
}));

// Address section
const AddressSection = styled("div")(({ theme }) => ({
  wordBreak: "break-all",
  [theme.breakpoints.down("tablet")]: {
    marginTop: theme.spacing(4),
  },
  [theme.breakpoints.up("tablet")]: {
    flex: "1 0 33.3333%",
  },
  "& > *": {
    marginBottom: 0,
  },
  "& > * + *": {
    marginTop: theme.spacing(2),
  },
}));

// Address content
const AddressContent = styled("div")(({ theme }) => ({
  "& > * + *": {
    marginTop: theme.spacing(2),
  },
}));

const FooterBottom = styled("div")(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  padding: `${theme.spacing(2)} 0`,
  [theme.breakpoints.up("desktop-s")]: {
    padding: `${theme.spacing(2.5)} 0`,
  },
}));

const FooterCopyright = styled(Typography)(({ theme }) => ({
  margin: 0,
  [theme.breakpoints.up("tablet")]: {
    textAlign: "right",
  },
}));

export const PageFooter = ({ navigation, salesRep, footerCopyright }: Props) => {
  const { t } = useTranslation();

  return (
    <>
      <Root>
        <div className="container">
          <Navigation>
            <LinksSection>
              {navigation.map((column, columnIndex) => (
                <LinksColumn key={columnIndex}>
                  {column.children.map((link) => (
                    <Fragment key={link.title}>
                      {link.url && (
                        <li>
                          <Link
                            variant={LinkType.SECONDARY}
                            className="js__gs-footer-link"
                            data-ga-name={link.gaLabel}
                            id={link.gaLabel}
                            href={link.url}
                            onClick={(event) => {
                              sendGAEvent({
                                eventCategory: "Navigation",
                                eventAction: "Footer",
                                eventLabel: event.currentTarget.dataset.gaName,
                              });
                            }}
                          >
                            {t(link.title)}
                          </Link>
                        </li>
                      )}
                      {!link.url && (
                        <li>
                          <Typography>{t(link.title)}</Typography>
                        </li>
                      )}
                    </Fragment>
                  ))}
                </LinksColumn>
              ))}
            </LinksSection>
            <AddressSection>
              <AddressContent dangerouslySetInnerHTML={{ __html: salesRep.fullAddress }} />

              {salesRep.phone && (
                <div>
                  <span>{t("footer.sales.rep.phone")}&nbsp;</span>
                  <span>{salesRep.phone}</span>
                </div>
              )}

              {salesRep.emails.map((email) => (
                <div key={email}>
                  <span>{t("footer.sales.rep.email")}&nbsp;</span>
                  <a href={`mailto:${email}`}>{email}</a>
                </div>
              ))}
            </AddressSection>
          </Navigation>
        </div>
      </Root>
      {footerCopyright && (
        <FooterBottom>
          <div className="container">
            <FooterCopyright variant="bodyMedium">{footerCopyright}</FooterCopyright>
          </div>
        </FooterBottom>
      )}
    </>
  );
};

export interface PageFooterProps {
  navigation: NavigationNodeDTO[];
  salesRep: {
    /**
     * Contains html
     */
    fullAddress: string;
    phone: string;
    emails: string[];
  };
  footerCopyright?: string;
}

export const createPageFooter = (props: PageFooterProps) => {
  return <PageFooter {...props} />;
};
